/**
 * DokumentOrdnerVerwaltung.jsx
 * Admin-Tab in /admin/personal für zentrale Ordner-Vorlage-Verwaltung.
 * Zeigt Baum-Struktur, Anlegen/Bearbeiten/Löschen (nur Admin).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import {
  Folder, FolderOpen, Plus, Edit2, Trash2, ChevronRight, ChevronDown,
  PenLine, FolderPlus, AlertTriangle, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function buildTree(folders) {
  const map = {};
  const roots = [];
  folders.forEach(f => { map[f.id] = { ...f, children: [] }; });
  folders.forEach(f => {
    if (f.parent_folder_id && map[f.parent_folder_id]) {
      map[f.parent_folder_id].children.push(map[f.id]);
    } else {
      roots.push(map[f.id]);
    }
  });
  return roots;
}

function FolderNode({ node, depth = 0, onEdit, onDelete, onAddChild }) {
  const [open, setOpen] = useState(true);
  const hasChildren = node.children?.length > 0;
  return (
    <div className={depth > 0 ? 'ml-5 border-l border-border/50 pl-3' : ''}>
      <div className={`flex items-center gap-2 py-2 px-2 rounded-lg hover:bg-muted/40 group ${!node.is_active ? 'opacity-50' : ''}`}
        data-testid={`folder-node-${node.id}`}>
        <button type="button" onClick={() => setOpen(v => !v)} className="text-muted-foreground">
          {hasChildren
            ? (open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />)
            : <span className="w-3.5 h-3.5 inline-block" />}
        </button>
        {open && hasChildren ? <FolderOpen className="h-4 w-4 text-amber-500 shrink-0" /> : <Folder className="h-4 w-4 text-amber-500 shrink-0" />}
        <span className="flex-1 text-sm font-medium truncate">{node.name}</span>
        {node.require_signature && (
          <Badge variant="outline" className="text-[10px] gap-0.5 px-1.5 py-0 text-violet-700 border-violet-300">
            <PenLine className="h-2.5 w-2.5" /> Unterschrift
          </Badge>
        )}
        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 shrink-0 transition-opacity">
          <button type="button" onClick={() => onAddChild(node)} title="Unterordner hinzufügen"
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-primary">
            <FolderPlus className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={() => onEdit(node)} title="Bearbeiten"
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-primary">
            <Edit2 className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={() => onDelete(node)} title="Löschen"
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {open && hasChildren && (
        <div>
          {node.children.map(child => (
            <FolderNode key={child.id} node={child} depth={depth + 1}
              onEdit={onEdit} onDelete={onDelete} onAddChild={onAddChild} />
          ))}
        </div>
      )}
    </div>
  );
}

export function DokumentOrdnerVerwaltung({ isAdmin }) {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // null | { mode:'create'|'edit', node?:object, parentId?:string }
  const [delDlg, setDelDlg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', require_signature: false });

  const fetchFolders = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/employee-documents/folders`, { headers: authH() });
      setFolders(r.data || []);
    } catch { toast.error('Fehler beim Laden der Ordner'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFolders(); }, []);

  const openCreate = (parentId = null) => {
    setForm({ name: '', description: '', require_signature: false });
    setDlg({ mode: 'create', parentId });
  };

  const openEdit = (node) => {
    setForm({ name: node.name, description: node.description || '', require_signature: node.require_signature });
    setDlg({ mode: 'edit', node });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name ist erforderlich'); return; }
    setSaving(true);
    try {
      if (dlg.mode === 'create') {
        const sortOrder = folders.filter(f => f.parent_folder_id === (dlg.parentId || null)).length;
        await axios.post(`${API}/employee-documents/folders`, {
          name: form.name.trim(),
          description: form.description || null,
          parent_folder_id: dlg.parentId || null,
          require_signature: form.require_signature,
          sort_order: sortOrder,
        }, { headers: authH() });
        toast.success('Ordner angelegt');
      } else {
        await axios.put(`${API}/employee-documents/folders/${dlg.node.id}`, {
          name: form.name.trim(),
          description: form.description || null,
          require_signature: form.require_signature,
        }, { headers: authH() });
        toast.success('Ordner aktualisiert');
      }
      setDlg(null);
      fetchFolders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/employee-documents/folders/${delDlg.id}`, { headers: authH() });
      toast.success('Ordner gelöscht');
      setDelDlg(null);
      fetchFolders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ordner konnte nicht gelöscht werden'); }
    finally { setSaving(false); }
  };

  const tree = buildTree(folders);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">Dokument-Ordner</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Ordner-Struktur gilt für alle Mitarbeiter. Dateien werden pro Mitarbeiter individuell hochgeladen.
              </p>
            </div>
            {isAdmin && (
              <Button size="sm" onClick={() => openCreate()} className="gap-1.5" data-testid="add-folder-btn">
                <Plus className="h-4 w-4" /> Ordner anlegen
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : folders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Folder className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium">Noch keine Ordner angelegt</p>
              {isAdmin && (
                <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={() => openCreate()}>
                  <Plus className="h-4 w-4" /> Ersten Ordner anlegen
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-0.5">
              {tree.map(node => (
                <FolderNode key={node.id} node={node}
                  onEdit={isAdmin ? openEdit : () => {}}
                  onDelete={isAdmin ? setDelDlg : () => {}}
                  onAddChild={isAdmin ? (n) => openCreate(n.id) : () => {}} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={!!dlg} onOpenChange={() => setDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Folder className="h-4 w-4 text-amber-500" />
              {dlg?.mode === 'create' ? 'Neuer Ordner' : 'Ordner bearbeiten'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="z.B. Infektionsschutzbelehrung" autoFocus data-testid="folder-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Beschreibung (optional)</Label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Kurzbeschreibung des Ordner-Inhalts" />
            </div>
            <div className="flex items-center justify-between rounded-xl border p-3">
              <div>
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <PenLine className="h-3.5 w-3.5 text-violet-600" /> Unterschrift erforderlich
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Mitarbeiter muss hochgeladene Dateien bestätigen/unterschreiben
                </p>
              </div>
              <Switch checked={form.require_signature}
                onCheckedChange={v => setForm(f => ({ ...f, require_signature: v }))}
                data-testid="folder-require-sig-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving || !form.name.trim()} data-testid="save-folder-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {dlg?.mode === 'create' ? 'Anlegen' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!delDlg} onOpenChange={() => setDelDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" /> Ordner löschen
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Ordner <strong>"{delDlg?.name}"</strong> löschen? Nur möglich wenn keine Dateien und keine Unterordner vorhanden sind.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelDlg(null)}>Abbrechen</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving} data-testid="confirm-delete-folder-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
